import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { 
  Users, 
  Calendar,
  Target,
  Megaphone,
  AlertTriangle,
  Download,
  ChevronRight,
  FileSpreadsheet,
  Archive,
  CheckCircle2,
  XCircle,
  Filter,
  TrendingUp,
  RefreshCw,
  Trash2
} from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "../utils/api";
import TeamTasksPanel from "../components/TeamTasksPanel";
import { etichettaTask } from "../utils/etichettaTask";

const TASK_TYPE_LABELS = {
  call_strategica: "Call Strategica",
  script_videomaker: "Script Videomaker",
  script_cliente: "Script Cliente",
  conferma_script: "Conferma Script",
  ped_programmazione: "PED + Programmazione",
  avvio_adv: "Avvio ADV",
  appuntamento_successivo: "Appuntamento Successivo"
};

const MONTH_NAMES = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"
];

export default function StatsPage({ user }) {
  const navigate = useNavigate();
  const [pmStats, setPmStats] = useState([]);
  const [pmAnalytics, setPmAnalytics] = useState(null);
  const [smmClientsServed, setSmmClientsServed] = useState(null);
  const [smmAdvActive, setSmmAdvActive] = useState(null);
  const [followerGrowth, setFollowerGrowth] = useState(null);
  const [smmUsers, setSmmUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // Mese selezionato per le statistiche (formato YYYY-MM)
  const now = new Date();
  const currentYearMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  const [selectedMonth, setSelectedMonth] = useState(currentYearMonth);
  const [selectedSmm, setSelectedSmm] = useState("all");
  
  // Dialog states
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [detailDialogTitle, setDetailDialogTitle] = useState("");
  const [detailDialogData, setDetailDialogData] = useState([]);
  const [detailDialogLoading, setDetailDialogLoading] = useState(false);
  
  // Follower detail dialog
  const [followerDialogOpen, setFollowerDialogOpen] = useState(false);
  const [followerDialogType, setFollowerDialogType] = useState("above");
  
  // Archive states
  const [archiveList, setArchiveList] = useState([]);
  const [selectedArchiveMonth, setSelectedArchiveMonth] = useState("");
  const [selectedSnapshot, setSelectedSnapshot] = useState(null);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [creatingSnapshot, setCreatingSnapshot] = useState(false);
  
  // Follower history states
  const [followerHistory, setFollowerHistory] = useState(null);
  const [loadingFollowerHistory, setLoadingFollowerHistory] = useState(false);
  const [clientHistoryDialogOpen, setClientHistoryDialogOpen] = useState(false);
  const [selectedClientHistory, setSelectedClientHistory] = useState(null);
  const [loadingClientHistory, setLoadingClientHistory] = useState(false);

  const isDirettore = user?.role === "direttore_tecnico" || user?.role === "direttore_commerciale";
  const isPM = user?.role === "pm" || isDirettore || !!user?.is_admin || user?.role === "amm" || user?.email === "info@diecixcento.com" || user?.is_responsabile_operativo || (user?.secondary_roles || []).some((r) => r === "pm" || r === "amm");

  const PERIOD_OPTIONS = [
    { value: "current_week", label: "Settimana corrente" },
    { value: "last_week", label: "Settimana scorsa" },
    { value: "current_month", label: "Mese corrente" },
    { value: "last_month", label: "Mese scorso" },
    { value: "next_month", label: "Mese prossimo" },
  ];

  // Generate month options for selector (last 24 months)
  const getMonthOptions = () => {
    const options = [];
    const now = new Date();
    for (let i = 0; i < 24; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const yearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
      const label = `${MONTH_NAMES[date.getMonth()]} ${date.getFullYear()}`;
      options.push({ value: yearMonth, label });
    }
    return options;
  };

  useEffect(() => {
    if (!isPM) {
      navigate("/dashboard");
      return;
    }
    fetchData();
    fetchArchiveList();
    checkAutoSnapshot();
  }, [isPM, navigate, selectedMonth, selectedSmm]);

  useEffect(() => {
    if (selectedArchiveMonth && archiveList.length > 0) {
      const archive = archiveList.find(a => a.year_month === selectedArchiveMonth);
      if (archive) {
        handleViewSnapshot(archive.snapshot_id);
      } else {
        setSelectedSnapshot(null);
      }
    }
  }, [selectedArchiveMonth]);

  const fetchData = async () => {
    try {
      const smmParam = selectedSmm !== "all" ? `&smm_id=${selectedSmm}` : "";
      const [pmRes, analyticsRes, clientsServedRes, advActiveRes, followerRes, smmRes] = await Promise.all([
        apiFetch("/dashboard/pm-stats"),
        apiFetch(`/stats/overview?month=${selectedMonth}${smmParam}`),
        apiFetch(`/stats/smm-clients-served-week${selectedSmm !== "all" ? `?smm_id=${selectedSmm}` : ""}`),
        apiFetch(`/stats/smm-adv-active-week${selectedSmm !== "all" ? `?smm_id=${selectedSmm}` : ""}`),
        apiFetch(`/follower-stats/growth/summary${selectedSmm !== "all" ? `?smm_id=${selectedSmm}` : ""}`),
        apiFetch("/users/smm")
      ]);
      
      if (pmRes.ok) setPmStats(await pmRes.json());
      if (analyticsRes.ok) setPmAnalytics(await analyticsRes.json());
      if (clientsServedRes.ok) setSmmClientsServed(await clientsServedRes.json());
      if (advActiveRes.ok) setSmmAdvActive(await advActiveRes.json());
      if (followerRes.ok) setFollowerGrowth(await followerRes.json());
      if (smmRes.ok) setSmmUsers(await smmRes.json());
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Errore nel caricamento dei dati");
    } finally {
      setLoading(false);
    }
  };

  const checkAutoSnapshot = async () => {
    try {
      const now = new Date();
      if (now.getDate() === 1) {
        await apiFetch("/stats/archive/auto-snapshot", { method: "POST" });
      }
    } catch (error) {
      // Silent fail
    }
  };

  const fetchArchiveList = async () => {
    try {
      const response = await apiFetch("/stats/archive");
      if (response.ok) {
        setArchiveList(await response.json());
      }
    } catch (error) {
      console.error("Error fetching archive:", error);
    }
  };

  const handleViewSnapshot = async (snapshotId) => {
    setLoadingArchive(true);
    try {
      const response = await apiFetch(`/stats/archive/${snapshotId}`);
      if (response.ok) {
        setSelectedSnapshot(await response.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento dello snapshot");
    } finally {
      setLoadingArchive(false);
    }
  };

  const showFollowerDetail = (type) => {
    setFollowerDialogType(type);
    setFollowerDialogOpen(true);
  };

  const fetchDetailData = async (endpoint, title) => {
    setDetailDialogTitle(title);
    setDetailDialogOpen(true);
    setDetailDialogLoading(true);
    setDetailDialogData([]);
    
    try {
      const response = await apiFetch(endpoint);
      if (response.ok) {
        const data = await response.json();
        setDetailDialogData(data);
      }
    } catch (error) {
      toast.error("Errore nel caricamento dei dettagli");
    } finally {
      setDetailDialogLoading(false);
    }
  };

  const handleExportStats = async () => {
    try {
      const response = await apiFetch("/stats/export/excel");
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `statistiche_smm_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Export completato");
      }
    } catch (error) {
      toast.error("Errore nell'export");
    }
  };

  const handleCreateSnapshot = async () => {
    setCreatingSnapshot(true);
    try {
      const response = await apiFetch("/stats/archive/snapshot", { method: "POST" });
      if (response.ok) {
        toast.success("Snapshot creato nell'archivio");
        fetchArchiveList();
      }
    } catch (error) {
      toast.error("Errore nella creazione dello snapshot");
    } finally {
      setCreatingSnapshot(false);
    }
  };

  const handleDeleteSnapshot = async (snapshotId) => {
    if (!confirm("Sei sicuro di voler eliminare questo snapshot?")) return;
    
    try {
      const response = await apiFetch(`/stats/archive/${snapshotId}`, { method: "DELETE" });
      if (response.ok) {
        toast.success("Snapshot eliminato");
        fetchArchiveList();
        if (selectedSnapshot?.snapshot_id === snapshotId) {
          setSelectedSnapshot(null);
        }
      } else {
        const error = await response.json();
        toast.error(error.detail || "Errore nell'eliminazione");
      }
    } catch (error) {
      toast.error("Errore nell'eliminazione dello snapshot");
    }
  };

  const handleExportExcelMensile = async (yearMonth) => {
    try {
      const response = await apiFetch(`/stats/export/excel-mensile/${yearMonth}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `statistiche_smm_${yearMonth}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Export Excel completato");
      } else {
        toast.error("Errore nell'export");
      }
    } catch (error) {
      toast.error("Errore nell'export Excel");
    }
  };

  const fetchFollowerHistory = async () => {
    setLoadingFollowerHistory(true);
    try {
      const response = await apiFetch("/follower-stats/history/all");
      if (response.ok) {
        setFollowerHistory(await response.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento storico follower");
    } finally {
      setLoadingFollowerHistory(false);
    }
  };

  const handleShowClientHistory = async (clientId) => {
    setClientHistoryDialogOpen(true);
    setLoadingClientHistory(true);
    setSelectedClientHistory(null);
    
    try {
      const response = await apiFetch(`/follower-stats/history/${clientId}`);
      if (response.ok) {
        setSelectedClientHistory(await response.json());
      }
    } catch (error) {
      toast.error("Errore nel caricamento");
    } finally {
      setLoadingClientHistory(false);
    }
  };

  const handleExportFollowerHistoryExcel = async () => {
    try {
      const response = await apiFetch("/follower-stats/export/excel");
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `storico_follower_${new Date().toISOString().split("T")[0]}.xlsx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        toast.success("Export Excel completato");
      } else {
        toast.error("Errore nell'export");
      }
    } catch (error) {
      toast.error("Errore nell'export Excel");
    }
  };

  const isAdmin = user?.email === "info@diecixcento.com" || user?.is_responsabile_operativo || isDirettore;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="stats-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-foreground">
            Statistiche
          </h1>
          <p className="text-muted-foreground mt-1">
            Clicca sui numeri per vedere i dettagli
          </p>
        </div>
        {isPM && (
          <div className="flex gap-2">
            <Button 
              variant="outline" 
              onClick={handleCreateSnapshot}
              disabled={creatingSnapshot}
              data-testid="create-snapshot-btn"
            >
              <FileSpreadsheet className="w-4 h-4 mr-2" />
              {creatingSnapshot ? "Creazione..." : "Salva Snapshot"}
            </Button>
            <Button onClick={handleExportStats} className="bg-emerald-600 hover:bg-emerald-700 text-white">
              <Download className="w-4 h-4 mr-2" />
              Esporta Excel
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="current" className="space-y-4">
        <TabsList className="flex-wrap h-auto">
          <TabsTrigger value="current">Mese Corrente</TabsTrigger>
          <TabsTrigger value="archive">Archivio Storico</TabsTrigger>
          <TabsTrigger value="follower-history" onClick={fetchFollowerHistory}>Storico Follower</TabsTrigger>
          <TabsTrigger value="team-tasks" data-testid="tab-team-tasks">Team & Task</TabsTrigger>
        </TabsList>

        {/* TAB: Mese Corrente */}
        <TabsContent value="current" className="space-y-6">
          {/* Period and SMM Filter */}
          <div className="flex flex-wrap items-center gap-3 p-3 bg-slate-50 rounded-lg">
            <Filter className="w-4 h-4 text-muted-foreground" />
            <span className="text-sm font-medium text-muted-foreground">Mese:</span>
            <Select value={selectedMonth} onValueChange={setSelectedMonth}>
              <SelectTrigger className="w-56" data-testid="month-filter">
                <Calendar className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {getMonthOptions().map(opt => (
                  <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <span className="text-sm font-medium text-muted-foreground ml-4">SMM:</span>
            <Select value={selectedSmm} onValueChange={setSelectedSmm}>
              <SelectTrigger className="w-48" data-testid="smm-filter">
                <Users className="w-4 h-4 mr-2" />
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutti gli SMM</SelectItem>
                {smmUsers.map(smm => (
                  <SelectItem key={smm.user_id} value={smm.user_id}>{smm.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Main Stats Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <Card 
              className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-blue-500"
              onClick={() => fetchDetailData("/stats/clients-girato-week", "Appuntamenti Girato - Settimana")}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-blue-600 mb-2">
                  <Calendar className="w-5 h-5" />
                  <span className="text-sm font-medium">Girato Settimana</span>
                </div>
                <p className="text-3xl font-bold">{pmAnalytics?.girato_week || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Clicca per dettagli</p>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-indigo-500"
              onClick={() => fetchDetailData("/stats/clients-girato-month", "Appuntamenti Girato - Mese")}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-indigo-600 mb-2">
                  <Target className="w-5 h-5" />
                  <span className="text-sm font-medium">Girato Mese</span>
                </div>
                <p className="text-3xl font-bold">{pmAnalytics?.girato_month || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Clicca per dettagli</p>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-emerald-500"
              onClick={() => fetchDetailData("/stats/adv-active", "ADV Attive")}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-emerald-600 mb-2">
                  <Megaphone className="w-5 h-5" />
                  <span className="text-sm font-medium">ADV Attive</span>
                </div>
                <p className="text-3xl font-bold">{pmAnalytics?.adv_active_total || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Clicca per dettagli</p>
              </CardContent>
            </Card>

            <Card 
              className="cursor-pointer hover:shadow-md transition-all border-l-4 border-l-red-500"
              onClick={() => setDetailDialogOpen(true) || setDetailDialogTitle("Tutte le Scadenze Mancate") || setDetailDialogData(pmAnalytics?.missed_tasks || [])}
            >
              <CardContent className="p-4">
                <div className="flex items-center gap-2 text-red-600 mb-2">
                  <AlertTriangle className="w-5 h-5" />
                  <span className="text-sm font-medium">Scadenze Mancate</span>
                </div>
                <p className="text-3xl font-bold">{pmAnalytics?.total_missed_deadlines || 0}</p>
                <p className="text-xs text-muted-foreground mt-1">Clicca per dettagli</p>
              </CardContent>
            </Card>
          </div>

          {/* Scadenze non rispettate per SMM */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-500" />
                Scadenze Non Rispettate per SMM
              </CardTitle>
              <CardDescription>Clicca sul numero per vedere le task scadute</CardDescription>
            </CardHeader>
            <CardContent>
              {pmAnalytics?.missed_by_smm?.length > 0 ? (
                <div className="space-y-2">
                  {pmAnalytics.missed_by_smm.map((smm, index) => (
                    <div 
                      key={index}
                      className="flex items-center justify-between p-3 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center">
                          <span className="text-sm font-semibold text-red-600">
                            {smm.name?.charAt(0) || "?"}
                          </span>
                        </div>
                        <span className="font-medium">{smm.name}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        onClick={() => fetchDetailData(`/stats/missed-by-smm/${smm.smm_id}`, `Task Scadute - ${smm.name}`)}
                      >
                        <span className="text-lg font-bold mr-2">{smm.missed}</span>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  Nessuna scadenza mancata! Ottimo lavoro!
                </p>
              )}
            </CardContent>
          </Card>

          {/* Carico Team SMM */}
          <Card>
            <CardHeader>
              <CardTitle className="font-heading text-lg flex items-center gap-2">
                <Users className="w-5 h-5 text-indigo-500" />
                Carico Team SMM
              </CardTitle>
              <CardDescription>Clicca sui numeri colorati per vedere le task</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-muted/50">
                    <tr>
                      <th className="text-left p-3 text-xs font-semibold uppercase">SMM</th>
                      <th className="text-center p-3 text-xs font-semibold uppercase">Clienti</th>
                      <th className="text-center p-3 text-xs font-semibold uppercase">
                        <span className="inline-flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-emerald-500"></div>
                          Verde
                        </span>
                      </th>
                      <th className="text-center p-3 text-xs font-semibold uppercase">
                        <span className="inline-flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-amber-500"></div>
                          Giallo
                        </span>
                      </th>
                      <th className="text-center p-3 text-xs font-semibold uppercase">
                        <span className="inline-flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-red-500"></div>
                          Rosso
                        </span>
                      </th>
                      <th className="text-center p-3 text-xs font-semibold uppercase">
                        <span className="inline-flex items-center gap-1">
                          <div className="w-2 h-2 rounded-full bg-zinc-700"></div>
                          Scaduto
                        </span>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {pmStats.map((smm, index) => (
                      <tr key={index} className="border-b border-slate-100">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center">
                              <span className="text-xs font-semibold text-indigo-600">
                                {smm.name?.charAt(0) || "?"}
                              </span>
                            </div>
                            <span className="font-medium">{smm.name}</span>
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <Badge variant="outline">{smm.num_clients}</Badge>
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50 font-bold"
                            onClick={() => fetchDetailData(`/stats/smm-tasks/${smm.user_id}/verde`, `Task Verdi - ${smm.name}`)}
                            disabled={!smm.verde}
                          >
                            {smm.verde || 0}
                          </Button>
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-amber-600 hover:text-amber-700 hover:bg-amber-50 font-bold"
                            onClick={() => fetchDetailData(`/stats/smm-tasks/${smm.user_id}/giallo`, `Task Gialle - ${smm.name}`)}
                            disabled={!smm.giallo}
                          >
                            {smm.giallo || 0}
                          </Button>
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-red-600 hover:text-red-700 hover:bg-red-50 font-bold"
                            onClick={() => fetchDetailData(`/stats/smm-tasks/${smm.user_id}/rosso`, `Task Rosse - ${smm.name}`)}
                            disabled={!smm.rosso}
                          >
                            {smm.rosso || 0}
                          </Button>
                        </td>
                        <td className="p-3 text-center">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="text-zinc-700 hover:text-zinc-800 hover:bg-zinc-100 font-bold"
                            onClick={() => fetchDetailData(`/stats/smm-tasks/${smm.user_id}/scaduto`, `Task Scadute - ${smm.name}`)}
                            disabled={!smm.scaduto}
                          >
                            {smm.scaduto || 0}
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* SMM Clients Served This Week */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Users className="w-5 h-5 text-blue-600" />
                Clienti Serviti per SMM (Settimana)
              </CardTitle>
              <CardDescription>
                Basato sulla data girato dei clienti
              </CardDescription>
            </CardHeader>
            <CardContent>
              {smmClientsServed?.data?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left p-3 text-xs font-semibold uppercase">SMM</th>
                        <th className="text-left p-3 text-xs font-semibold uppercase">Clienti Serviti</th>
                        <th className="text-left p-3 text-xs font-semibold uppercase">Nomi Clienti</th>
                      </tr>
                    </thead>
                    <tbody>
                      {smmClientsServed.data.map((smm, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="p-3 font-medium">{smm.smm_name}</td>
                          <td className="p-3"><Badge className="bg-blue-100 text-blue-700">{smm.clients_served}</Badge></td>
                          <td className="p-3 text-sm text-muted-foreground">{smm.client_names?.join(", ") || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">Nessun dato disponibile per questa settimana</p>
              )}
            </CardContent>
          </Card>

          {/* SMM ADV Active This Week */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <Megaphone className="w-5 h-5 text-emerald-600" />
                Campagne ADV Attivate per SMM (Settimana)
              </CardTitle>
              <CardDescription>
                Basato sulle task "Avvio ADV" completate
              </CardDescription>
            </CardHeader>
            <CardContent>
              {smmAdvActive?.data?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left p-3 text-xs font-semibold uppercase">SMM</th>
                        <th className="text-left p-3 text-xs font-semibold uppercase">ADV Attivate</th>
                        <th className="text-left p-3 text-xs font-semibold uppercase">Campagne</th>
                      </tr>
                    </thead>
                    <tbody>
                      {smmAdvActive.data.map((smm, idx) => (
                        <tr key={idx} className="border-b border-slate-100">
                          <td className="p-3 font-medium">{smm.smm_name}</td>
                          <td className="p-3"><Badge className="bg-emerald-100 text-emerald-700">{smm.adv_active}</Badge></td>
                          <td className="p-3 text-sm text-muted-foreground">{smm.campaign_names?.join(", ") || "-"}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-muted-foreground text-sm">Nessuna ADV attivata questa settimana</p>
              )}
            </CardContent>
          </Card>

          {/* Follower Growth Summary */}
          <Card className="border border-slate-200">
            <CardHeader>
              <CardTitle className="font-heading flex items-center gap-2">
                <TrendingUp className="w-5 h-5 text-purple-600" />
                Crescita Follower Clienti
              </CardTitle>
              <CardDescription>
                Confronto mese corrente vs mese precedente (soglia: ±2%). Clicca sulle card per vedere i dettagli.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                <div 
                  className="p-4 bg-emerald-50 rounded-lg cursor-pointer hover:bg-emerald-100 transition-colors"
                  onClick={() => showFollowerDetail("above")}
                  data-testid="follower-above-card"
                >
                  <p className="text-sm text-emerald-600 font-medium">Sopra +2%</p>
                  <p className="text-2xl font-bold text-emerald-700">{followerGrowth?.summary?.above_count || 0}</p>
                  <p className="text-xs text-emerald-600 mt-1">Clicca per dettagli →</p>
                </div>
                <div 
                  className="p-4 bg-red-50 rounded-lg cursor-pointer hover:bg-red-100 transition-colors"
                  onClick={() => showFollowerDetail("below")}
                  data-testid="follower-below-card"
                >
                  <p className="text-sm text-red-600 font-medium">Sotto +2%</p>
                  <p className="text-2xl font-bold text-red-700">{followerGrowth?.summary?.below_count || 0}</p>
                  <p className="text-xs text-red-600 mt-1">Clicca per dettagli →</p>
                </div>
                <div 
                  className="p-4 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 transition-colors"
                  onClick={() => showFollowerDetail("no_data")}
                  data-testid="follower-nodata-card"
                >
                  <p className="text-sm text-slate-600 font-medium">Senza dati</p>
                  <p className="text-2xl font-bold text-slate-700">{followerGrowth?.summary?.no_data_count || 0}</p>
                  <p className="text-xs text-slate-600 mt-1">Clicca per dettagli →</p>
                </div>
              </div>

              {(!followerGrowth?.above_2_percent?.length && !followerGrowth?.below_2_percent?.length) && (
                <p className="text-muted-foreground text-sm">Nessun dato follower disponibile. I SMM devono inserire i follower mensili per ogni cliente.</p>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Archivio Storico */}
        <TabsContent value="archive" className="space-y-4">
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <Archive className="w-5 h-5 text-indigo-600" />
                    Archivio Storico Statistiche
                  </CardTitle>
                  <CardDescription>
                    Snapshot mensili salvati automaticamente il 1° del mese
                  </CardDescription>
                </div>
                <Select value={selectedArchiveMonth} onValueChange={setSelectedArchiveMonth}>
                  <SelectTrigger className="w-48" data-testid="archive-month-selector">
                    <Calendar className="w-4 h-4 mr-2" />
                    <SelectValue placeholder="Seleziona mese..." />
                  </SelectTrigger>
                  <SelectContent>
                    {getMonthOptions().map(opt => (
                      <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </CardHeader>
            <CardContent>
              {archiveList.length === 0 ? (
                <div className="text-center py-8 text-muted-foreground">
                  <Archive className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun archivio disponibile</p>
                  <p className="text-sm mt-2">
                    Gli snapshot vengono salvati automaticamente il 1° di ogni mese
                  </p>
                </div>
              ) : (
                <>
                  {/* Archive Cards Grid */}
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
                    {archiveList.map((snapshot, index) => {
                      const [year, month] = (snapshot.year_month || "").split("-");
                      const monthIndex = parseInt(month) - 1;
                      return (
                        <Card 
                          key={snapshot.snapshot_id}
                          className={`border cursor-pointer transition-colors hover:border-indigo-300 ${
                            selectedSnapshot?.snapshot_id === snapshot.snapshot_id 
                              ? 'border-indigo-500 bg-indigo-50' 
                              : 'border-slate-200'
                          }`}
                          onClick={() => handleViewSnapshot(snapshot.snapshot_id)}
                          data-testid={`archive-card-${index}`}
                        >
                          <CardContent className="pt-4">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-semibold">
                                  {monthIndex >= 0 ? MONTH_NAMES[monthIndex] : ""} {year}
                                </p>
                                <p className="text-sm text-muted-foreground">
                                  {snapshot.auto_generated ? "Auto" : "Manuale"}
                                </p>
                              </div>
                              <Calendar className="w-5 h-5 text-indigo-600" />
                            </div>
                            <div className="mt-3 pt-3 border-t border-slate-200 text-sm">
                              <p>Clienti: {snapshot.total_clients || 0}</p>
                              <p>Task: {snapshot.total_tasks || 0}</p>
                              <p>SMM: {snapshot.total_smm || 0}</p>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>

                  {/* Selected Snapshot Details */}
                  {loadingArchive ? (
                    <div className="flex items-center justify-center py-8">
                      <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
                    </div>
                  ) : selectedSnapshot && (
                    <div className="pt-6 border-t border-slate-200">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="font-semibold flex items-center gap-2">
                          <FileSpreadsheet className="w-4 h-4" />
                          Dettaglio Snapshot: {selectedSnapshot.year_month}
                        </h3>
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleExportExcelMensile(selectedSnapshot.year_month)}
                            data-testid="export-excel-mensile-btn"
                          >
                            <Download className="w-4 h-4 mr-1" />
                            Export Excel
                          </Button>
                          {isAdmin && (
                            <Button
                              variant="destructive"
                              size="sm"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteSnapshot(selectedSnapshot.snapshot_id);
                              }}
                              data-testid="delete-snapshot-btn"
                            >
                              <Trash2 className="w-4 h-4 mr-1" />
                              Elimina
                            </Button>
                          )}
                        </div>
                      </div>
                      
                      {/* Summary Cards */}
                      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Clienti Totali</p>
                          <p className="font-bold text-lg">{selectedSnapshot.total_clients}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Task Totali</p>
                          <p className="font-bold text-lg">{selectedSnapshot.total_tasks}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-muted-foreground">SMM Attivi</p>
                          <p className="font-bold text-lg">{selectedSnapshot.total_smm}</p>
                        </div>
                        <div className="p-3 bg-slate-50 rounded-lg">
                          <p className="text-xs text-muted-foreground">Data Creazione</p>
                          <p className="font-bold text-sm">
                            {new Date(selectedSnapshot.created_at).toLocaleDateString("it-IT")}
                          </p>
                        </div>
                      </div>

                      {/* Follower Growth Summary */}
                      {selectedSnapshot.follower_growth_summary && (
                        <div className="mb-6">
                          <h4 className="font-medium mb-3 flex items-center gap-2 text-purple-600">
                            <TrendingUp className="w-4 h-4" />
                            Crescita Follower del Mese
                          </h4>
                          <div className="grid grid-cols-3 gap-4">
                            <div className="p-3 bg-emerald-50 rounded-lg border border-emerald-200">
                              <p className="text-sm text-emerald-600">Sopra +2%</p>
                              <p className="text-xl font-bold text-emerald-700">
                                {selectedSnapshot.follower_growth_summary.above_2_percent || 0}
                              </p>
                            </div>
                            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
                              <p className="text-sm text-red-600">Sotto +2%</p>
                              <p className="text-xl font-bold text-red-700">
                                {selectedSnapshot.follower_growth_summary.below_2_percent || 0}
                              </p>
                            </div>
                            <div className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                              <p className="text-sm text-slate-600">Senza dati</p>
                              <p className="text-xl font-bold text-slate-700">
                                {selectedSnapshot.follower_growth_summary.no_data_count || 0}
                              </p>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Follower Data per Client */}
                      {selectedSnapshot.follower_data?.length > 0 && (
                        <div className="mb-6">
                          <h4 className="font-medium mb-3 flex items-center gap-2 text-purple-600">
                            <Users className="w-4 h-4" />
                            Follower per Cliente ({selectedSnapshot.year_month})
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b bg-purple-50">
                                  <th className="text-left p-3">Cliente</th>
                                  <th className="text-left p-3">SMM</th>
                                  <th className="text-center p-3">Instagram</th>
                                  <th className="text-center p-3">TikTok</th>
                                  <th className="text-center p-3">Facebook</th>
                                  <th className="text-center p-3">LinkedIn</th>
                                  <th className="text-center p-3">Totale</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedSnapshot.follower_data
                                  .filter(f => !f.no_data)
                                  .map((client, idx) => (
                                  <tr key={idx} className="border-b border-slate-100">
                                    <td className="p-3 font-medium">{client.client_name}</td>
                                    <td className="p-3 text-muted-foreground">{client.smm_name}</td>
                                    <td className="p-3 text-center">{client.platforms?.instagram || "-"}</td>
                                    <td className="p-3 text-center">{client.platforms?.tiktok || "-"}</td>
                                    <td className="p-3 text-center">{client.platforms?.facebook || "-"}</td>
                                    <td className="p-3 text-center">{client.platforms?.linkedin || "-"}</td>
                                    <td className="p-3 text-center font-bold text-purple-600">
                                      {client.total_followers?.toLocaleString() || 0}
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                          {selectedSnapshot.follower_data.filter(f => f.no_data).length > 0 && (
                            <p className="text-sm text-muted-foreground mt-2">
                              {selectedSnapshot.follower_data.filter(f => f.no_data).length} clienti senza dati follower
                            </p>
                          )}
                        </div>
                      )}

                      {/* SMM Stats Table */}
                      {selectedSnapshot.smm_stats?.length > 0 && (
                        <div>
                          <h4 className="font-medium mb-3 flex items-center gap-2 text-indigo-600">
                            <Users className="w-4 h-4" />
                            Statistiche per SMM
                          </h4>
                          <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                              <thead>
                                <tr className="border-b bg-slate-50">
                                  <th className="text-left p-3">SMM</th>
                                  <th className="text-center p-3">Clienti</th>
                                  <th className="text-center p-3">Task Totali</th>
                                  <th className="text-center p-3">Completati</th>
                                  <th className="text-center p-3">Scadenze Mancate</th>
                                  <th className="text-center p-3">ADV Attive</th>
                                </tr>
                              </thead>
                              <tbody>
                                {selectedSnapshot.smm_stats.map((smm, idx) => (
                                  <tr key={idx} className="border-b border-slate-100">
                                    <td className="p-3">
                                      <div>
                                        <p className="font-medium">{smm.smm_name}</p>
                                        <p className="text-xs text-muted-foreground">{smm.smm_email}</p>
                                      </div>
                                    </td>
                                    <td className="p-3 text-center">
                                      <Badge variant="outline">{smm.num_clients}</Badge>
                                    </td>
                                    <td className="p-3 text-center">{smm.total_tasks}</td>
                                    <td className="p-3 text-center">
                                      <span className="text-emerald-600 font-medium">{smm.completed_tasks}</span>
                                    </td>
                                    <td className="p-3 text-center">
                                      {smm.missed_deadlines > 0 ? (
                                        <Badge className="bg-red-100 text-red-700">{smm.missed_deadlines}</Badge>
                                      ) : (
                                        <span className="text-muted-foreground">0</span>
                                      )}
                                    </td>
                                    <td className="p-3 text-center">
                                      <Badge className="bg-emerald-100 text-emerald-700">{smm.adv_active}</Badge>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Storico Follower */}
        <TabsContent value="follower-history" className="space-y-4">
          <Card className="border border-slate-200">
            <CardHeader>
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                  <CardTitle className="font-heading flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-purple-600" />
                    Storico Follower per Cliente
                  </CardTitle>
                  <CardDescription>
                    Clicca su un cliente per vedere il dettaglio mese per mese
                  </CardDescription>
                </div>
                <Button 
                  onClick={handleExportFollowerHistoryExcel}
                  className="bg-purple-600 hover:bg-purple-700 text-white"
                  data-testid="export-follower-history-btn"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Esporta Excel
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {loadingFollowerHistory ? (
                <div className="flex items-center justify-center py-8">
                  <RefreshCw className="w-6 h-6 animate-spin text-purple-600" />
                </div>
              ) : followerHistory ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-purple-50">
                        <th className="text-left p-3 sticky left-0 bg-purple-50">Cliente</th>
                        <th className="text-left p-3">SMM</th>
                        {followerHistory.months.map(month => {
                          const [y, m] = month.split("-");
                          return (
                            <th key={month} className="text-center p-3 min-w-[100px]">
                              {MONTH_NAMES[parseInt(m) - 1]} {y}
                            </th>
                          );
                        })}
                        <th className="text-center p-3 min-w-[100px]">Crescita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {followerHistory.clients.map((client, idx) => (
                        <tr 
                          key={client.client_id} 
                          className="border-b border-slate-100 hover:bg-slate-50 cursor-pointer"
                          onClick={() => handleShowClientHistory(client.client_id)}
                          data-testid={`follower-row-${idx}`}
                        >
                          <td className="p-3 font-medium sticky left-0 bg-white">
                            {client.client_name}
                          </td>
                          <td className="p-3 text-muted-foreground">{client.smm_name}</td>
                          {followerHistory.months.map(month => {
                            const data = client.monthly_data[month];
                            return (
                              <td key={month} className="p-3 text-center">
                                {data ? (
                                  <div>
                                    <span className="font-medium">{data.total?.toLocaleString()}</span>
                                    {data.growth !== null && (
                                      <span className={`text-xs ml-1 ${data.growth >= 2 ? 'text-emerald-600' : data.growth >= 0 ? 'text-amber-600' : 'text-red-600'}`}>
                                        ({data.growth > 0 ? '+' : ''}{data.growth}%)
                                      </span>
                                    )}
                                  </div>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                            );
                          })}
                          <td className="p-3 text-center">
                            {client.overall_growth !== null ? (
                              <Badge className={`${
                                client.overall_growth >= 2 
                                  ? 'bg-emerald-100 text-emerald-700' 
                                  : client.overall_growth >= 0 
                                    ? 'bg-amber-100 text-amber-700' 
                                    : 'bg-red-100 text-red-700'
                              }`}>
                                {client.overall_growth > 0 ? '+' : ''}{client.overall_growth}%
                              </Badge>
                            ) : (
                              <span className="text-muted-foreground">-</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <TrendingUp className="w-12 h-12 mx-auto mb-4 opacity-50" />
                  <p>Nessun dato follower disponibile</p>
                  <p className="text-sm mt-2">
                    I SMM devono inserire i follower mensili per ogni cliente
                  </p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* TAB: Team & Task — storico completati + report produttività */}
        <TabsContent value="team-tasks" className="space-y-4">
          <TeamTasksPanel user={user} />
        </TabsContent>
      </Tabs>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading">{detailDialogTitle}</DialogTitle>
          </DialogHeader>
          
          {detailDialogLoading ? (
            <div className="py-8 text-center">
              <div className="w-8 h-8 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto"></div>
              <p className="text-muted-foreground mt-2">Caricamento...</p>
            </div>
          ) : detailDialogData.length > 0 ? (
            <div className="space-y-2">
              {detailDialogData.map((item, index) => (
                <div key={index} className="p-3 bg-slate-50 rounded-lg">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium">{item.client_name}</p>
                      {item.task_type && (
                        <p className="text-sm text-muted-foreground">
                          {etichettaTask(item, TASK_TYPE_LABELS)}
                        </p>
                      )}
                      {item.smm_name && (
                        <p className="text-xs text-indigo-600 mt-1">
                          SMM: {item.smm_name}
                        </p>
                      )}
                    </div>
                    <div className="text-right">
                      {item.due_date && (
                        <p className="text-sm">
                          Scadenza: {new Date(item.due_date).toLocaleDateString("it-IT")}
                        </p>
                      )}
                      {item.data_girato && (
                        <p className="text-sm">
                          Girato: {new Date(item.data_girato).toLocaleDateString("it-IT")}
                        </p>
                      )}
                      {item.completed_at && (
                        <p className="text-sm text-emerald-600">
                          Completato: {new Date(item.completed_at).toLocaleDateString("it-IT")}
                        </p>
                      )}
                      {item.status && (
                        <Badge variant="outline" className="mt-1">
                          {item.status === "da_fare" ? "Da fare" : 
                           item.status === "in_corso" ? "In corso" : "Completato"}
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Nessun dato trovato
            </p>
          )}
        </DialogContent>
      </Dialog>

      {/* Follower Detail Dialog */}
      <Dialog open={followerDialogOpen} onOpenChange={setFollowerDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              {followerDialogType === "above" && (
                <>
                  <CheckCircle2 className="w-5 h-5 text-emerald-600" />
                  Clienti con crescita ≥ +2%
                </>
              )}
              {followerDialogType === "below" && (
                <>
                  <XCircle className="w-5 h-5 text-red-600" />
                  Clienti con crescita &lt; +2%
                </>
              )}
              {followerDialogType === "no_data" && (
                <>
                  <AlertTriangle className="w-5 h-5 text-slate-600" />
                  Clienti senza dati follower
                </>
              )}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            {followerDialogType === "above" && followerGrowth?.above_2_percent?.map((client, idx) => (
              <div key={idx} className="p-4 bg-emerald-50 rounded-lg border border-emerald-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-lg">{client.client_name}</span>
                  <Badge className="bg-emerald-500 text-white text-lg">+{client.avg_growth}%</Badge>
                </div>
                {client.platforms && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    {Object.entries(client.platforms).map(([platform, data]) => (
                      <div key={platform} className="text-sm bg-white p-2 rounded">
                        <p className="font-medium capitalize">{platform}</p>
                        <p className="text-emerald-600">+{data.growth}%</p>
                        <p className="text-xs text-muted-foreground">{data.previous} → {data.current}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {followerDialogType === "below" && followerGrowth?.below_2_percent?.map((client, idx) => (
              <div key={idx} className="p-4 bg-red-50 rounded-lg border border-red-200">
                <div className="flex justify-between items-center mb-2">
                  <span className="font-semibold text-lg">{client.client_name}</span>
                  <Badge className="bg-red-500 text-white text-lg">{client.avg_growth > 0 ? '+' : ''}{client.avg_growth}%</Badge>
                </div>
                {client.platforms && (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                    {Object.entries(client.platforms).map(([platform, data]) => (
                      <div key={platform} className="text-sm bg-white p-2 rounded">
                        <p className="font-medium capitalize">{platform}</p>
                        <p className={data.growth >= 2 ? "text-emerald-600" : "text-red-600"}>{data.growth > 0 ? '+' : ''}{data.growth}%</p>
                        <p className="text-xs text-muted-foreground">{data.previous} → {data.current}</p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}

            {followerDialogType === "no_data" && followerGrowth?.no_data?.map((client, idx) => (
              <div key={idx} className="p-3 bg-slate-50 rounded-lg border border-slate-200">
                <span className="font-medium">{client.client_name}</span>
                <p className="text-sm text-muted-foreground">Nessun dato follower inserito</p>
              </div>
            ))}

            {((followerDialogType === "above" && !followerGrowth?.above_2_percent?.length) ||
              (followerDialogType === "below" && !followerGrowth?.below_2_percent?.length) ||
              (followerDialogType === "no_data" && !followerGrowth?.no_data?.length)) && (
              <p className="text-center text-muted-foreground py-4">Nessun cliente in questa categoria</p>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Client Follower History Dialog */}
      <Dialog open={clientHistoryDialogOpen} onOpenChange={setClientHistoryDialogOpen}>
        <DialogContent className="sm:max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-purple-600" />
              Storico Follower - {selectedClientHistory?.client_name}
            </DialogTitle>
          </DialogHeader>
          
          {loadingClientHistory ? (
            <div className="py-8 text-center">
              <RefreshCw className="w-8 h-8 animate-spin mx-auto text-purple-600" />
              <p className="text-muted-foreground mt-2">Caricamento...</p>
            </div>
          ) : selectedClientHistory ? (
            <div className="space-y-4">
              {/* Summary */}
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                <div>
                  <p className="text-sm text-muted-foreground">SMM: {selectedClientHistory.smm_name}</p>
                  <p className="text-sm text-muted-foreground">Mesi di dati: {selectedClientHistory.history?.length || 0}</p>
                </div>
                {selectedClientHistory.overall_growth !== null && (
                  <div className="text-right">
                    <p className="text-sm text-muted-foreground">Crescita Totale</p>
                    <Badge className={`text-lg ${
                      selectedClientHistory.overall_growth >= 2 
                        ? 'bg-emerald-500 text-white' 
                        : selectedClientHistory.overall_growth >= 0 
                          ? 'bg-amber-500 text-white' 
                          : 'bg-red-500 text-white'
                    }`}>
                      {selectedClientHistory.overall_growth > 0 ? '+' : ''}{selectedClientHistory.overall_growth}%
                    </Badge>
                  </div>
                )}
              </div>

              {/* Monthly Data Table */}
              {selectedClientHistory.history?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left p-3">Mese</th>
                        <th className="text-center p-3">Instagram</th>
                        <th className="text-center p-3">TikTok</th>
                        <th className="text-center p-3">Facebook</th>
                        <th className="text-center p-3">YouTube</th>
                        <th className="text-center p-3">LinkedIn</th>
                        <th className="text-center p-3">Totale</th>
                        <th className="text-center p-3">Crescita</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedClientHistory.history.map((month, idx) => {
                        const [y, m] = (month.month || "").split("-");
                        const monthLabel = m ? `${MONTH_NAMES[parseInt(m) - 1]} ${y}` : month.month;
                        return (
                          <tr key={idx} className="border-b border-slate-100">
                            <td className="p-3 font-medium">{monthLabel}</td>
                            <td className="p-3 text-center">{month.platforms?.instagram?.toLocaleString() || "-"}</td>
                            <td className="p-3 text-center">{month.platforms?.tiktok?.toLocaleString() || "-"}</td>
                            <td className="p-3 text-center">{month.platforms?.facebook?.toLocaleString() || "-"}</td>
                            <td className="p-3 text-center">{month.platforms?.youtube?.toLocaleString() || "-"}</td>
                            <td className="p-3 text-center">{month.platforms?.linkedin?.toLocaleString() || "-"}</td>
                            <td className="p-3 text-center font-bold text-purple-600">
                              {month.total?.toLocaleString() || 0}
                            </td>
                            <td className="p-3 text-center">
                              {month.growth !== null ? (
                                <Badge className={`${
                                  month.growth >= 2 
                                    ? 'bg-emerald-100 text-emerald-700' 
                                    : month.growth >= 0 
                                      ? 'bg-amber-100 text-amber-700' 
                                      : 'bg-red-100 text-red-700'
                                }`}>
                                  {month.growth > 0 ? '+' : ''}{month.growth}%
                                </Badge>
                              ) : (
                                <span className="text-muted-foreground">-</span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-center text-muted-foreground py-8">
                  Nessun dato follower disponibile per questo cliente
                </p>
              )}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">
              Nessun dato disponibile
            </p>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
